import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import EmptyState from '../../components/EmptyState/EmptyState';
import { getPresets, getMinBudget, getActiveSessions, getHistory, startSession, stopSession, getTemplates, updateTemplate, startTemplate } from '../../services/TodayService';
import { getTopSymbols, getSymbolAge } from '../../services/SymbolsService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';
import { fmtMoney, fmtPct, fmtPrice, fmtQty } from '../../utils/format';

const RISK_META = {
    SAFE: { color: 'success', dot: 'gh-dot-success' },
    MEDIUM: { color: 'warning', dot: 'gh-dot-warning' },
    FAST: { color: 'danger', dot: 'gh-dot-danger' }
};

// Small shield SVG used on the "never sells at a loss" note.
const ShieldIcon = () => (
    <svg className="icon icon-xs me-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.352.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
);

function Today() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const tv = (id, values) => formatMessage({ id }, values);
    const history2 = useHistory();

    const [presets, setPresets] = useState([]);
    const [topPairs, setTopPairs] = useState([]);
    const [topLoaded, setTopLoaded] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [history, setHistory] = useState([]);
    const [forms, setForms] = useState({}); // riskLevel -> { symbol, amount, maxTrades, mode }
    const [confirm, setConfirm] = useState(null); // { payload, preset } for LIVE confirmation
    const [minBudgets, setMinBudgets] = useState({}); // symbol -> minBudget (USDC)
    const [ages, setAges] = useState({}); // symbol -> ageDays (lazy, cached server-side)
    const [templates, setTemplates] = useState([]);   // system-default templates (PAUSED)
    const [editTpl, setEditTpl] = useState(null);      // template currently being edited (modal)
    const [notification, setNotification] = useState({});

    const refreshActive = useCallback(() => {
        getActiveSessions()
            .then(setSessions)
            .catch(err => logger.error(err.response ? err.response.data : err.message));
        getHistory()
            .then(setHistory)
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }, []);

    function loadTemplates() {
        getTemplates().then(setTemplates).catch(err => logger.error(err.response ? err.response.data : err.message));
    }

    useEffect(() => {
        getPresets().then(setPresets).catch(err => logger.error(err.message));
        loadTemplates();
        // Deep link from an alert (?symbol=BTCUSDC&mode=SAFE) → preselect that pair
        // on the matching preset card (mode), so the user just hits Start.
        try {
            const qp = new URLSearchParams(window.location.search);
            const dlSymbol = (qp.get('symbol') || '').toUpperCase();
            const dlMode = (qp.get('mode') || '').toUpperCase();
            if (dlSymbol) {
                const level = ['SAFE', 'MEDIUM', 'FAST'].includes(dlMode) ? dlMode : 'SAFE';
                setForm(level, { symbol: dlSymbol });
                getMinBudget(dlSymbol)
                    .then(mb => setMinBudgets(prev => ({ ...prev, [dlSymbol]: mb.minBudget })))
                    .catch(() => { });
            }
        } catch (e) { /* ignore */ }
        // "Today" buys dips → pull dips first, then the rest of the liquid majors.
        getTopSymbols(12, 'USDC', 'dip')
            .then(p => {
                const pairs = Array.isArray(p) ? p : [];
                setTopPairs(pairs);
                // Fetch each pair's minimum tranche budget (anti lot-size-dust).
                pairs.forEach(pr => getMinBudget(pr.symbol)
                    .then(mb => setMinBudgets(prev => ({ ...prev, [pr.symbol]: mb.minBudget })))
                    .catch(() => { }));
                // Lazily fetch each displayed pair's trading age (cached server-side).
                // Never block the panel — failures just omit age for that pair.
                pairs.forEach(pr => getSymbolAge(pr.symbol)
                    .then(a => { if (a && a.ageDays != null) setAges(prev => ({ ...prev, [pr.symbol]: a.ageDays })); })
                    .catch(() => { }));
            })
            .catch(err => logger.error(err.message))
            .finally(() => setTopLoaded(true));
        refreshActive();
        const timer = setInterval(refreshActive, 5000);
        return () => clearInterval(timer);
    }, [refreshActive]);

    function formFor(preset, idx) {
        const defaults = {
            symbol: (topPairs[idx] && topPairs[idx].symbol) || (topPairs[0] && topPairs[0].symbol) || 'BTCUSDC',
            amount: '',
            maxTrades: preset.maxTrades,
            takeProfit: preset.takeProfitPct,   // client-adjustable, defaults to preset
            dcaStep: preset.dcaStepPct,         // client-adjustable, defaults to preset
            mode: 'LIVE' // LIVE-only platform (owner decision 2026-07-29)
        };
        // Always merge stored partial state over defaults so symbol/mode/params
        // are never lost when only one field was changed.
        return { ...defaults, ...(forms[preset.riskLevel] || {}) };
    }

    function setForm(riskLevel, patch) {
        setForms(prev => ({ ...prev, [riskLevel]: { ...prev[riskLevel], ...patch } }));
    }

    function onStart(preset, idx) {
        const f = formFor(preset, idx);
        const amount = parseFloat(f.amount);
        if (!amount || amount <= 0) return setNotification({ type: 'error', text: t('today.enterAmount') });
        const min = minBudgets[f.symbol];
        if (min && amount < min) return setNotification({ type: 'error', text: `${t('today.minPerTrade')} ${min} USDC` });

        const payload = { riskLevel: preset.riskLevel, symbol: f.symbol, budget: amount, maxTrades: f.maxTrades, takeProfitPct: f.takeProfit, dcaStepPct: f.dcaStep, mode: f.mode };
        // LIVE uses real money — require explicit confirmation in a modal.
        if (f.mode === 'LIVE') return setConfirm({ payload, preset });
        doStart(payload);
    }

    function doStart(payload) {
        setConfirm(null);
        startSession(payload)
            .then(() => { setNotification({ type: 'success', text: t('today.started') }); refreshActive(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onStop(id) {
        stopSession(id)
            .then(() => { setNotification({ type: 'success', text: t('today.stopped') }); refreshActive(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onSaveTemplate(tpl) {
        const payload = {
            symbol: tpl.symbol,
            budget: parseFloat(tpl.budget),
            maxTrades: parseInt(tpl.maxTrades),
            takeProfitPct: parseFloat(tpl.takeProfitPct),
            dcaStepPct: parseFloat(tpl.dcaStepPct),
            mode: tpl.mode
        };
        updateTemplate(tpl.id, payload)
            .then(() => { setNotification({ type: 'success', text: t('today.template.saved') }); setEditTpl(null); loadTemplates(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onStartTemplate(tpl) {
        startTemplate(tpl.id, {})
            .then(() => { setNotification({ type: 'success', text: t('today.template.started') }); refreshActive(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    const activeFor = (riskLevel) => sessions.find(s => s.riskLevel === riskLevel && s.status === 'RUNNING');

    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    const nowMs = Date.now();
    const num = (v) => parseFloat(v) || 0;
    const pnlToday = history.filter(s => new Date(s.createdAt) >= dayStart).reduce((a, s) => a + num(s.pnl), 0);
    const pnlWeek = history.filter(s => nowMs - new Date(s.createdAt).getTime() <= 7 * 86400000).reduce((a, s) => a + num(s.pnl), 0);
    const fmtPnl = (v) => `${v >= 0 ? '+' : ''}${fmtMoney(v)}`;

    // Compact USD volume, e.g. 34_400_000 -> "$34.4M".
    const fmtVolume = (v) => {
        const n = parseFloat(v) || 0;
        if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
        if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
        if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
        return `$${n.toFixed(0)}`;
    };

    // Human age from ageDays, e.g. "3y 2m" / "5m" / "new".
    const fmtAge = (days) => {
        if (days == null) return null;
        const years = Math.floor(days / 365);
        const months = Math.floor((days % 365) / 30);
        if (years > 0) return tv('today.pairs.age.years', { y: years, m: months });
        if (months > 0) return tv('today.pairs.age.months', { m: months });
        return t('today.pairs.age.new');
    };

    // Click a top pair → open the Dashboard chart + live monitor on that coin.
    function openChart(symbol) {
        try { localStorage.setItem('goldhub_chart_symbol', symbol); } catch (e) { /* ignore */ }
        history2.push('/dashboard');
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="py-4">
                    <h1 className="h4 mb-1">{t('today.title')}</h1>
                    <p className="mb-0 text-muted">{t('today.subtitle')}</p>
                </div>

                {(topLoaded || (topPairs && topPairs.length > 0)) && (
                    <div className="card card-body border-0 shadow mb-4">
                        <small className="text-muted text-uppercase d-block mb-3">{t('riskProfiles.topPairsToday')}</small>
                        {topPairs.length === 0 ? (
                            <EmptyState
                                title={t('today.pairs.noDips')}
                                text={t('today.pairs.noDipsHint')}
                                cta={{ label: t('today.pairs.notifyMe'), onClick: () => history2.push('/alerts?enable=1') }}
                            />
                        ) : (
                        <div className="gh-grid">
                            {topPairs.map(p => {
                                const rangePct = Math.round((p.rangePos != null ? p.rangePos : 0) * 100);
                                const age = fmtAge(ages[p.symbol]);
                                return (
                                    <div key={p.symbol}>
                                        <div
                                            className="card bg-dark text-white h-100 border-0 shadow-sm"
                                            role="button"
                                            tabIndex={0}
                                            title={t('today.pairs.openChart')}
                                            onClick={() => openChart(p.symbol)}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openChart(p.symbol); } }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <strong className="fs-6">{p.symbol}</strong>
                                                    <span className="d-flex align-items-center gap-1">
                                                        {p.isDip ? <span className="gh-badge gh-badge-success">{t('today.pairs.dip')}{p.score != null ? ` · ${p.score}` : ''}{p.entryConfirmed ? ' ✓' : ''}</span> : null}
                                                        <span className={`badge ${p.changePct >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                            {p.changePct >= 0 ? '+' : ''}{fmtPct(p.changePct)}%
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between small text-muted">
                                                    <span>{t('today.pairs.volume')}</span>
                                                    <span className="text-white">{fmtVolume(p.quoteVolume)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between small text-muted">
                                                    <span>{t('today.pairs.volatility')}</span>
                                                    <span className="text-warning">{fmtPct(p.volatilityPct)}%</span>
                                                </div>
                                                {age && (
                                                    <div className="d-flex justify-content-between small text-muted">
                                                        <span>{t('today.pairs.tradingSince')}</span>
                                                        <span className="text-white">{age}</span>
                                                    </div>
                                                )}
                                                <div className="mt-2">
                                                    <div className="d-flex justify-content-between small text-muted mb-1">
                                                        <span>{t('today.pairs.rangePos')}</span>
                                                        {p.rangePos <= 0.4 && <span className="text-info">▼ {t('today.pairs.nearLow')}</span>}
                                                    </div>
                                                    <div className="progress" style={{ height: '6px', background: '#2e3650' }}>
                                                        <div
                                                            className={`progress-bar ${p.rangePos <= 0.4 ? 'bg-info' : 'bg-secondary'}`}
                                                            role="progressbar"
                                                            style={{ width: `${rangePct}%` }}
                                                            aria-valuenow={rangePct}
                                                            aria-valuemin={0}
                                                            aria-valuemax={100}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                )}

                <div className="gh-grid gh-grid-wide mb-4">
                    {presets.map((preset, idx) => {
                        const meta = RISK_META[preset.riskLevel] || { color: 'secondary', dot: 'gh-dot-muted' };
                        const f = formFor(preset, idx);
                        const active = activeFor(preset.riskLevel);
                        return (
                            <div key={preset.riskLevel}>
                                <div className={`card border-${meta.color} ${active ? 'shadow-lg' : 'shadow'} h-100`}>
                                    <div className="card-header d-flex align-items-center justify-content-between">
                                        <h5 className="mb-0"><span className={`gh-dot ${meta.dot}`} />{t('today.risk.' + preset.riskLevel)}</h5>
                                        {active && <span className="badge bg-success">{t('today.running')}</span>}
                                    </div>
                                    <div className="card-body">
                                        <p className={`small mb-3 ${preset.riskLevel === 'SAFE' ? 'text-success' : 'text-warning'}`}>
                                            <ShieldIcon />{t(preset.riskLevel === 'SAFE' ? 'today.noLossNote' : 'today.timeStopNote')}
                                        </p>

                                        {active ? (
                                            <div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.pair')}</small><span className="fw-bold">{active.symbol}</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.mode')}</small><span className={`badge bg-${active.mode === 'LIVE' ? 'danger' : 'secondary'}`}>{active.mode}</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.takeProfit')} / {t('today.dcaStep')}</small><span>+{fmtPct(active.takeProfitPct)}% / −{fmtPct(active.dcaStepPct)}%</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.trades')}</small><span>{active.tradesCount}/{active.maxTrades} ({active.winCount}W / {active.lossCount || 0}L)</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.pnl')}</small><span className={parseFloat(active.pnl) >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>{fmtMoney(active.pnl)} {active.quote}</span></div>
                                                {active.openQty && parseFloat(active.openQty) > 0 && (
                                                    <div className="d-flex justify-content-between"><small className="text-muted">{t('today.openPosition')}</small><span>{fmtQty(active.openQty)} @ {fmtPrice(active.openEntry)}</span></div>
                                                )}
                                                {active.lastMessage && <p className="small text-muted mt-2 mb-0">{active.lastMessage}</p>}
                                                <div className="d-grid mt-3">
                                                    <button className="btn btn-danger" onClick={() => onStop(active.id)}>{t('today.stop')}</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="mb-2">
                                                    <label className="form-label small text-muted">{t('today.pair')}</label>
                                                    <select className="form-select form-select-sm" value={f.symbol} onChange={e => setForm(preset.riskLevel, { symbol: e.target.value })}>
                                                        {topPairs.map(p => <option key={p.symbol} value={p.symbol}>{p.isDip ? '▼ ' : ''}{p.symbol} ({p.changePct >= 0 ? '+' : ''}{fmtPct(p.changePct)}%){p.entryConfirmed ? ' ✓' : ''}</option>)}
                                                        {!topPairs.some(p => p.symbol === f.symbol) && <option value={f.symbol}>{f.symbol}</option>}
                                                    </select>
                                                </div>
                                                <div className="row">
                                                    <div className="col-7 mb-1">
                                                        <label className="form-label small text-muted">{t('today.amount')} (USDC)</label>
                                                        <input className="form-control form-control-sm" type="number" min={minBudgets[f.symbol] || 1} step="1" placeholder={minBudgets[f.symbol] ? `≥ ${minBudgets[f.symbol]}` : '100'} value={f.amount} onChange={e => setForm(preset.riskLevel, { amount: e.target.value })} />
                                                    </div>
                                                    <div className="col-5 mb-1">
                                                        <label className="form-label small text-muted">{t('today.maxBuys')}</label>
                                                        <input className="form-control form-control-sm" type="number" min="1" max="100" value={f.maxTrades} onChange={e => setForm(preset.riskLevel, { maxTrades: parseInt(e.target.value) || 1 })} />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 mb-1">
                                                        <label className="form-label small text-muted">{t('today.takeProfit')} %</label>
                                                        <input className="form-control form-control-sm" type="number" min="2.2" max="50" step="0.1" value={f.takeProfit} onChange={e => setForm(preset.riskLevel, { takeProfit: e.target.value })} />
                                                    </div>
                                                    <div className="col-6 mb-1">
                                                        <label className="form-label small text-muted">{t('today.dcaStep')} %</label>
                                                        <input className="form-control form-control-sm" type="number" min="0.5" max="50" step="0.1" value={f.dcaStep} onChange={e => setForm(preset.riskLevel, { dcaStep: e.target.value })} />
                                                    </div>
                                                </div>
                                                <p className="small text-muted mb-2">
                                                    {minBudgets[f.symbol] && <>{t('today.minPerTrade')} {minBudgets[f.symbol]} USDC · </>}
                                                    {f.amount > 0 && <>{t('today.maxTotal')} {fmtMoney(parseFloat(f.amount) * (parseInt(f.maxTrades) || 1))} USDC</>}
                                                </p>
                                                <p className="small text-danger mb-2"><span className="gh-dot gh-dot-danger" />{t('today.liveWarn')}</p>
                                                <div className="d-grid">
                                                    <button className={`btn btn-${meta.color}`} onClick={() => onStart(preset, idx)}>{t('today.start')}</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {templates && templates.length > 0 && (
                    <div className="mb-2">
                        <div className="py-2">
                            <h5 className="mb-1">{t('today.template.heading')}</h5>
                            <p className="mb-0 text-muted small">{t('today.template.subtitle')}</p>
                        </div>
                        <div className="gh-grid gh-grid-wide mb-4">
                            {templates.map(tpl => {
                                const meta = RISK_META[tpl.riskLevel] || { color: 'secondary', dot: 'gh-dot-muted' };
                                return (
                                    <div key={tpl.id}>
                                        <div className={`card border-${meta.color} shadow h-100`}>
                                            <div className="card-header d-flex align-items-center justify-content-between">
                                                <h5 className="mb-0"><span className={`gh-dot ${meta.dot}`} />{tpl.templateName || t('today.risk.' + tpl.riskLevel)}</h5>
                                                <span className="badge bg-info">{t('common.paused')}</span>
                                            </div>
                                            <div className="card-body">
                                                <span className="badge bg-secondary mb-3">{t('today.template.badge')}</span>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.pair')}</small><span className="fw-bold">{tpl.symbol}</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.amount')}</small><span>{fmtMoney(tpl.budget)} {tpl.quote}</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.maxBuys')}</small><span>{tpl.maxTrades}</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.takeProfit')} / {t('today.dcaStep')}</small><span>+{fmtPct(tpl.takeProfitPct)}% / −{fmtPct(tpl.dcaStepPct)}%</span></div>
                                                <div className="d-flex justify-content-between"><small className="text-muted">{t('today.mode')}</small><span className={`badge bg-${tpl.mode === 'LIVE' ? 'danger' : 'secondary'}`}>{tpl.mode}</span></div>
                                            </div>
                                            <div className="card-footer d-flex justify-content-between">
                                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditTpl({ ...tpl })}>{t('today.template.edit')}</button>
                                                <button className={`btn btn-sm btn-${meta.color}`} onClick={() => onStartTemplate(tpl)}>{t('today.template.start')}</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="card card-body border-0 shadow mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                        <h5 className="mb-0">{t('today.history')}</h5>
                        <div className="text-end">
                            <span className="me-3"><small className="text-muted">{t('today.pnlToday')}</small> <span className={pnlToday >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>{fmtPnl(pnlToday)} USDC</span></span>
                            <span><small className="text-muted">{t('today.pnlWeek')}</small> <span className={pnlWeek >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>{fmtPnl(pnlWeek)} USDC</span></span>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover table-sm gh-table-stack">
                            <thead>
                                <tr>
                                    <th>{t('today.date')}</th>
                                    <th>{t('today.mode')}</th>
                                    <th>{t('today.pair')}</th>
                                    <th>{t('today.trades')}</th>
                                    <th className="num">{t('today.history.buyPrice')}</th>
                                    <th className="num">{t('today.history.sellPrice')}</th>
                                    <th className="num">{t('today.history.fees')}</th>
                                    <th>{t('today.pnl')}</th>
                                    <th>{t('common.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history && history.length ? history.map(s => (
                                    <tr key={s.id}>
                                        <td data-label={t('today.date')}><small>{new Date(s.createdAt).toLocaleString()}</small></td>
                                        <td data-label={t('today.mode')}><span className={`badge bg-${s.mode === 'LIVE' ? 'danger' : 'secondary'}`}>{s.mode}</span> {t('today.risk.' + s.riskLevel)}</td>
                                        <td data-label={t('today.pair')}>{s.symbol}</td>
                                        <td data-label={t('today.trades')}>{s.tradesCount} ({s.winCount}W / {s.lossCount || 0}L)</td>
                                        {/* Trade-journal columns: sessions from before the journal have no
                                            rows → null → "—". buyPrice = weighted avg of all buys,
                                            sellPrice = last sell fill, feesTotal = every fee paid (quote). */}
                                        <td data-label={t('today.history.buyPrice')} className="num">{s.buyPrice != null ? fmtPrice(s.buyPrice) : '—'}</td>
                                        <td data-label={t('today.history.sellPrice')} className="num">{s.sellPrice != null ? fmtPrice(s.sellPrice) : '—'}</td>
                                        <td data-label={t('today.history.fees')} className="num text-muted">{s.feesTotal != null ? fmtMoney(s.feesTotal) : '—'}</td>
                                        <td data-label={t('today.pnl')} className={num(s.pnl) >= 0 ? 'text-success' : 'text-danger'}>{fmtPnl(num(s.pnl))}</td>
                                        <td data-label={t('common.status')}>{s.status === 'RUNNING' ? <span className="badge bg-success">{t('today.running')}</span> : <span className="text-muted">{s.status}</span>}</td>
                                    </tr>
                                )) : (<tr><td colSpan={9} className="text-muted">{t('today.noSessions')}</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>

                <Footer />
            </main>

            {confirm && (
                <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">{t('today.live')} — {t('today.confirmTitle')}</h5>
                                <button type="button" className="btn-close" onClick={() => setConfirm(null)}></button>
                            </div>
                            <div className="modal-body">
                                <p>{t('today.liveConfirm')}</p>
                                <ul className="mb-0">
                                    <li>{t('today.pair')}: <strong>{confirm.payload.symbol}</strong></li>
                                    <li>{t('today.amount')}: <strong>{fmtMoney(confirm.payload.budget)} USDC</strong> × {confirm.payload.maxTrades} {t('today.maxBuys')} = <strong>{fmtMoney(parseFloat(confirm.payload.budget) * parseInt(confirm.payload.maxTrades))} USDC {t('today.maxTotal')}</strong></li>
                                    <li>{t('today.takeProfit')}: <strong className="text-success">+{fmtPct(confirm.payload.takeProfitPct)}%</strong> · {t('today.dcaStep')}: <strong>−{fmtPct(confirm.payload.dcaStepPct)}%</strong></li>
                                    <li className={confirm.payload.riskLevel === 'SAFE' ? 'text-success' : 'text-warning'}><ShieldIcon />{t(confirm.payload.riskLevel === 'SAFE' ? 'today.noLossNote' : 'today.timeStopNote')}</li>
                                </ul>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>{t('common.cancel')}</button>
                                <button className="btn btn-danger" onClick={() => doStart(confirm.payload)}>{t('today.confirmStart')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editTpl && (
                <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('today.template.editTitle')} — {editTpl.templateName || t('today.risk.' + editTpl.riskLevel)}</h5>
                                <button type="button" className="btn-close" onClick={() => setEditTpl(null)}></button>
                            </div>
                            <div className="modal-body">
                                <span className="badge bg-secondary mb-3">{t('today.template.badge')}</span>
                                <div className="mb-2">
                                    <label className="form-label small text-muted">{t('today.pair')}</label>
                                    <select className="form-select form-select-sm" value={editTpl.symbol} onChange={e => setEditTpl({ ...editTpl, symbol: e.target.value })}>
                                        {!topPairs.some(p => p.symbol === editTpl.symbol) && <option value={editTpl.symbol}>{editTpl.symbol}</option>}
                                        {topPairs.map(p => <option key={p.symbol} value={p.symbol}>{p.symbol} ({p.changePct >= 0 ? '+' : ''}{fmtPct(p.changePct)}%)</option>)}
                                    </select>
                                </div>
                                <div className="row">
                                    <div className="col-7 mb-2">
                                        <label className="form-label small text-muted">{t('today.amount')} (USDC)</label>
                                        <input className="form-control form-control-sm" type="number" min="1" step="1" value={editTpl.budget} onChange={e => setEditTpl({ ...editTpl, budget: e.target.value })} />
                                    </div>
                                    <div className="col-5 mb-2">
                                        <label className="form-label small text-muted">{t('today.maxBuys')}</label>
                                        <input className="form-control form-control-sm" type="number" min="1" max="100" value={editTpl.maxTrades} onChange={e => setEditTpl({ ...editTpl, maxTrades: e.target.value })} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6 mb-2">
                                        <label className="form-label small text-muted">{t('today.takeProfit')} %</label>
                                        <input className="form-control form-control-sm" type="number" min="2.2" max="50" step="0.1" value={editTpl.takeProfitPct} onChange={e => setEditTpl({ ...editTpl, takeProfitPct: e.target.value })} />
                                    </div>
                                    <div className="col-6 mb-2">
                                        <label className="form-label small text-muted">{t('today.dcaStep')} %</label>
                                        <input className="form-control form-control-sm" type="number" min="0.5" max="50" step="0.1" value={editTpl.dcaStepPct} onChange={e => setEditTpl({ ...editTpl, dcaStepPct: e.target.value })} />
                                    </div>
                                </div>
                                <span className="gh-badge gh-badge-danger">{t('today.live')}</span>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setEditTpl(null)}>{t('common.cancel')}</button>
                                <button className="btn btn-primary" onClick={() => onSaveTemplate(editTpl)}>{t('today.template.save')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Today;
